import {
  detectAccountType,
  matchAccountTypeToTransferRefundType,
  resetAccountNumberAndCodeIfNeed,
  setAccountNumberAndCodeByType,
} from "@edaz/utils/bankAccount";
import type { IPagingFilter } from "@frui.ts/data";
import { attachAutomaticDirtyWatcher, isDirty, resetDirty } from "@frui.ts/dirtycheck";
import { bound } from "@frui.ts/helpers";
import { watchBusy } from "@frui.ts/screens";
import { attachAutomaticValidator, isValid, validateAll } from "@frui.ts/validation";
import * as Sentry from "@sentry/react";
import type { IPagedResults } from "data/types";
import type Approvable from "entities/approvable";
import BankCodeType from "entities/bankCodeType";
import { RefundPaymentInformationValidationRulesFactory } from "entities/chargeRefund";
import {
  DomesticCounterpartyBankValidationRules,
  ForeignCounterpartyBankValidationRulesFactory,
  SepaCounterpartyBankValidationRules,
} from "entities/counterpartyBank";
import type RefundTransferPaymentResponse from "entities/refundTransferPaymentResponse";
import TransferType from "entities/transferType";
import type { IRefundWithPayment } from "entities/types";
import { IRefund } from "entities/types";
import cloneDeep from "lodash/cloneDeep";
import { computed, observable, reaction } from "mobx";
import type RefundRepository from "repositories/refundRepository";
import type EventBus from "services/eventBus";
import type LocalizationService from "services/localizationService";
import type OptionsService from "services/optionsService";
import type RefundsApprovalService from "services/refundsApprovalService";
import type AppRouter from "services/router";
import type SecurityService from "services/securityService";
import { serializeAccountNumber } from "utils/filters";
import { removeInvisibleCharacters } from "utils/stringUtils";
import type AlertsViewModel from "../../../alertsViewModel";
import type CountrySelectViewModel from "../../../countrySelectViewModel";
import { excludeRefundPaymentPropertiesByType } from "../utils/utils";
import RefundDetailViewModel from "./refundDetailViewModel";
import TransferPaymentType from "../../../../entities/transferPaymentType";
import type EnumService from "../../../../services/enumService";
import type PriceListService from "services/priceListService";

export default abstract class EditableRefundDetailViewModel<
  TRefund extends IRefundWithPayment,
  TRefundList extends Approvable
> extends RefundDetailViewModel<TRefund, TRefundList> {
  protected abstract fetchData(): Promise<IPagedResults<TRefundList>>;

  protected abstract fetchDetail(id: string): Promise<TRefund>;

  protected abstract updatePayment(id: string, data: RefundTransferPaymentResponse): Promise<any>;

  protected abstract onRefundSaved(): Promise<void>;

  @observable protected originalPaymentType: TransferPaymentType;
  protected unsubscriptions: Array<() => void> = [];
  public countrySelectViewModel: CountrySelectViewModel;

  @computed
  get refundTypeOptions() {
    return this.optionsService.internalEnumOptions(TransferType).filter(item => {
      const id = item.id as TransferType;
      const transferType = this.item.refundPayment.transferType;

      if (id === TransferType.Unspecified) {
        return false;
      } else if (transferType === TransferPaymentType.Foreign && id === TransferType.Foreign) {
        return true;
      } else if (transferType !== TransferPaymentType.Foreign && id !== TransferType.Foreign) {
        return true;
      }

      return false;
    });
  }

  @computed
  get bankCodeOptions() {
    return this.optionsService.internalEnumOptions(BankCodeType);
  }

  @computed
  get isRefundPaymentValid() {
    return isValid(this.item.refundPayment) && isValid(this.item.refundPayment.counterpartyBank);
  }

  @computed
  get isPaymentEditable() {
    return this.recognizeIfPaymentIsEditable(this.item);
  }

  @computed
  get isPaymentTypeEditable() {
    return this.isPaymentEditable && this.refundTypeOptions.length > 1;
  }

  @computed
  get hasUnsavedChanges() {
    return (
      isDirty(this.item.refundPayment) ||
      isDirty(this.item.refundPayment.counterpartyBank) ||
      isDirty(this.item.refundPayment.counterpartyBank.bankAccount)
    );
  }

  constructor(
    protected itemId: string,
    protected pagingFilter: IPagingFilter,
    protected localizationService: LocalizationService,
    protected appRouter: AppRouter,
    protected approvalService: RefundsApprovalService,
    protected repository: RefundRepository,
    protected optionsService: OptionsService,
    protected securityService: SecurityService,
    protected eventBus: EventBus,
    protected countrySelectViewModelFactory: ReturnType<typeof CountrySelectViewModel.Factory>,
    protected enumService: EnumService,
    protected priceListService: PriceListService,
    public alertsViewModel: AlertsViewModel
  ) {
    super(
      itemId,
      pagingFilter,
      localizationService,
      appRouter,
      approvalService,
      optionsService,
      repository,
      eventBus,
      enumService,
      priceListService,
      alertsViewModel
    );
    this.countrySelectViewModel = this.countrySelectViewModelFactory("code");
  }

  protected async loadDetail(): Promise<TRefund> {
    const item = await this.fetchDetail(this.itemId);
    if (!this.items) {
      this.items = await this.loadList();
    }

    this.clearUnsubscriptions();
    attachAutomaticValidator(
      item.refundPayment,
      RefundPaymentInformationValidationRulesFactory(item.refundPayment),
      this.recognizeIfPaymentIsEditable(item)
    );
    attachAutomaticDirtyWatcher(item.refundPayment);
    attachAutomaticDirtyWatcher(item.refundPayment.counterpartyBank);
    attachAutomaticDirtyWatcher(item.refundPayment.counterpartyBank.bankAccount);
    this.unsubscriptions.push(
      reaction(
        () => item.refundPayment.transferType,
        transferType => this.onRefundPaymentTransferTypeChanged(transferType, item),
        { fireImmediately: true }
      )
    );
    this.unsubscriptions.push(
      reaction(
        () => item.refundPayment.counterpartyBank.codeType,
        codeType => this.onCodeTypeChanged(item, codeType),
        { fireImmediately: true }
      )
    );
    this.unsubscriptions.push(
      reaction(
        () => item.refundPayment.counterpartyCityAddress,
        counterpartyCityAddress => {
          if (counterpartyCityAddress) {
            item.refundPayment.counterpartyCityAddress = removeInvisibleCharacters(counterpartyCityAddress);
          }
        },
        { fireImmediately: true }
      )
    );
    this.originalPaymentType = item.refundPayment.transferType;
    this.item = item;

    resetDirty(this.item.refundPayment);
    resetDirty(this.item.refundPayment.counterpartyBank);
    resetDirty(this.item.refundPayment.counterpartyBank.bankAccount);
    return this.item;
  }

  protected recognizeIfPaymentIsEditable(item: TRefund) {
    return (
      item.approvals.length === 0 &&
      this.securityService.allowTo("finance", "write") &&
      (this.securityService.allowTo("finance.commandoperating", "write") ||
        this.securityService.allowTo("finance.chiefaccounting", "write") ||
        this.securityService.allowTo("finance.factualauditing", "write"))
    );
  }

  protected onDeactivate(isClosing: boolean): Promise<any> | void {
    if (isClosing) {
      this.clearUnsubscriptions();
    }
    return super.onDeactivate(isClosing);
  }

  @bound
  private onRefundPaymentTransferTypeChanged(transferType: TransferPaymentType, item: TRefund) {
    resetAccountNumberAndCodeIfNeed(
      item.refundPayment.counterpartyBank,
      transferType as any,
      item.refundPayment.counterpartyBank.codeType
    );

    switch (transferType) {
      case TransferPaymentType.Foreign:
        attachAutomaticValidator(
          item.refundPayment.counterpartyBank,
          ForeignCounterpartyBankValidationRulesFactory(item.refundPayment.counterpartyBank),
          this.recognizeIfPaymentIsEditable(item)
        );
        break;

      case TransferPaymentType.Sepa:
        item.refundPayment.counterpartyBank.codeType = BankCodeType.Bic;
        attachAutomaticValidator(
          item.refundPayment.counterpartyBank,
          SepaCounterpartyBankValidationRules,
          this.recognizeIfPaymentIsEditable(item)
        );
        break;

      case TransferPaymentType.Domestic:
        item.refundPayment.counterpartyBank.codeType = undefined;
        attachAutomaticValidator(
          item.refundPayment.counterpartyBank,
          DomesticCounterpartyBankValidationRules,
          this.recognizeIfPaymentIsEditable(item)
        );
        break;
    }
  }

  @bound
  @watchBusy
  async onSaveTransferRefundPaymentClick() {
    if (!validateAll([this.item, this.item.refundPayment, this.item.refundPayment.counterpartyBank])) {
      return;
    }

    try {
      const data = cloneDeep(this.item.refundPayment);
      const serializedAccountNumber = serializeAccountNumber(data.counterpartyBank.accountNumber);
      if (serializedAccountNumber) {
        data.counterpartyBank.accountNumber = serializedAccountNumber;
      }
      await this.updatePayment(this.itemId, excludeRefundPaymentPropertiesByType(data));
      await this.onRefundSaved();
    } catch (error) {
      Sentry.captureException(error);
    }
  }

  @bound
  @watchBusy
  async onCancelTransferRefundModificationClick() {
    await this.loadDetail();
  }

  @bound
  onApproveClick(item: IRefund) {
    return async () => {
      await this.approvalService.approve(this.repository, item, {
        title: this.translate(`confirmApproval.${this.hasUnsavedChanges ? "titleUnsavedChanges" : "title"}`),
        confirm: this.translate("confirmApproval.confirm"),
        approved: this.translate("approved"),
      });
    };
  }

  @bound
  onInvalidAccountNumberPasted(value: string, event: ClipboardEvent) {
    const accountType = detectAccountType(value);
    const desiredTransferType = matchAccountTypeToTransferRefundType(accountType) as any as TransferPaymentType;

    if (
      [TransferPaymentType.Sepa, TransferPaymentType.Domestic].includes(this.item.refundPayment.transferType) &&
      desiredTransferType !== TransferPaymentType.Foreign &&
      desiredTransferType !== this.item.refundPayment.transferType
    ) {
      this.item.refundPayment.transferType = desiredTransferType;
      setAccountNumberAndCodeByType(value, accountType, this.item.refundPayment.counterpartyBank);
      // We need to prevent default beacuse we're setting the input value above
      event.preventDefault();
    }
  }

  @bound
  private onCodeTypeChanged(item: TRefund, codeType?: BankCodeType) {
    // TODO deal with TransferType
    resetAccountNumberAndCodeIfNeed(item.refundPayment.counterpartyBank, item.refundPayment.transferType as any, codeType);
  }

  protected clearUnsubscriptions() {
    this.unsubscriptions.forEach(unsubscribe => unsubscribe());
    this.unsubscriptions = [];
  }
}
